import React, { useState, useEffect, useRef } from 'react';
import { useRAGStore, AVAILABLE_CHUNK_TOOLS, AVAILABLE_LANGUAGES } from '../store/ragStore';
import { Plus, Trash2, FolderOpen, Folder, RefreshCw, Settings, ChevronDown } from 'lucide-react';

const ContextSelector: React.FC = () => {
  const {
    contexts,
    currentContext,
    selectedChunkTool,
    selectedLanguage,
    createContext,
    deleteContext,
    setCurrentContext,
    setSelectedChunkTool,
    setSelectedLanguage,
    loadContexts,
    clearCurrentContext,
  } = useRAGStore();

  // Ensure contexts is always an array
  const safeContexts = contexts || [];

  const [newContextId, setNewContextId] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleCreateContext = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const contextId = newContextId.trim();
    if (!contextId) {
      alert('Please enter a Context ID');
      return;
    }

    setIsCreating(true);
    try {
      await createContext(contextId);
      setNewContextId('');
      setShowCreateForm(false);
      alert(`Context "${contextId}" created successfully`);
    } catch (error) {
      alert('Failed to create context');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteContext = async (contextId: string) => {
    if (window.confirm('Are you sure you want to delete this context? This action cannot be undone.')) {
      await deleteContext(contextId);
      // If we're deleting the current context, clear it
      if (currentContext?.contextId === contextId) {
        clearCurrentContext();
      }
    }
  };

  const handleSelectContext = (context: any) => {
    setCurrentContext(context);
    setIsDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="border-b border-gray-200 pb-3">
        <h2 className="text-base font-semibold text-gray-900">Contexts</h2>
        <p className="text-xs text-gray-600 mt-1">Select a context to work with</p>
      </div>

      {/* Current Context Display */}
      {currentContext && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
          <div className="flex items-center space-x-2 mb-2">
            <FolderOpen className="h-3 w-3 text-blue-600" />
            <span className="text-xs font-medium text-blue-900">Current Context</span>
          </div>
          <p className="text-xs font-medium text-blue-900 mb-2 truncate">{currentContext.contextId}</p>
          <button
            onClick={clearCurrentContext}
            className="text-xs text-blue-600 hover:text-blue-800 underline"
          >
            Change Context
          </button>
        </div>
      )}

      {/* Create Context Button */}
      <div className="flex justify-between items-center">
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="btn-primary flex items-center space-x-2 text-xs px-2 py-1.5"
        >
          <Plus className="h-3 w-3" />
          <span>New Context</span>
        </button>
        
        <button
          onClick={() => loadContexts()}
          className="btn-secondary p-1.5"
          title="Refresh contexts"
        >
          <RefreshCw className="h-3 w-3" />
        </button>
      </div>

      {/* Create Context Form */}
      {showCreateForm && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <form onSubmit={handleCreateContext} className="space-y-3">
            <div>
              <label htmlFor="contextId" className="block text-xs font-medium text-gray-700 mb-1">
                Context Name
              </label>
              <input
                id="contextId"
                type="text"
                value={newContextId}
                onChange={(e) => setNewContextId(e.target.value)}
                className="input-field text-xs py-1.5"
                placeholder="e.g., project_ko"
                disabled={isCreating}
              />
            </div>
            
            <div className="flex space-x-2">
              <button
                type="submit"
                disabled={isCreating}
                className="btn-primary flex-1 text-xs py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? 'Creating...' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="btn-secondary text-xs py-1.5 px-2"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Contexts List */}
      <div className="space-y-2">
        <h3 className="text-xs font-medium text-gray-700">Available Contexts</h3>
        
        {safeContexts.length === 0 ? (
          <div className="text-center py-3">
            <Folder className="h-5 w-5 text-gray-400 mx-auto mb-2" />
            <p className="text-xs text-gray-500">No contexts available</p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Custom Dropdown Selector */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={toggleDropdown}
                className="input-field text-xs py-1.5 pr-8 w-full text-left flex items-center justify-between bg-white border border-gray-300 rounded-lg hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <span className={currentContext ? 'text-gray-900' : 'text-gray-500'}>
                  {currentContext ? currentContext.contextId : 'Select a context'}
                </span>
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {safeContexts.map((context) => (
                    <div
                      key={context.id}
                      className="flex items-center justify-between p-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                    >
                      <div 
                        className="flex items-center space-x-2 flex-1 cursor-pointer"
                        onClick={() => handleSelectContext(context)}
                      >
                        <Folder className="h-3 w-3 text-gray-600" />
                        <span className="text-xs font-medium text-gray-900">
                          {context.contextId}
                        </span>
                      </div>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteContext(context.contextId);
                        }}
                        className="btn-danger text-xs px-1.5 py-1 ml-2 hover:bg-red-600"
                        title="Delete context"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Current Context Display */}
            {currentContext && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                <div className="flex items-center space-x-2">
                  <FolderOpen className="h-3 w-3 text-blue-600" />
                  <span className="text-xs font-medium text-blue-900">
                    Current: {currentContext.contextId}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Quick Settings */}
      <div className="border-t border-gray-200 pt-3">
        <h3 className="text-xs font-medium text-gray-700 mb-2">Quick Settings</h3>
        
        <div className="space-y-2">
          <div>
            <label htmlFor="chunkTool" className="block text-xs font-medium text-gray-700 mb-1">
              Chunk System
            </label>
            <select
              id="chunkTool"
              value={selectedChunkTool.name}
              onChange={(e) => {
                const tool = AVAILABLE_CHUNK_TOOLS.find(t => t.name === e.target.value);
                if (tool) setSelectedChunkTool(tool);
              }}
              className="input-field text-xs py-1.5"
            >
              {AVAILABLE_CHUNK_TOOLS.map((tool) => (
                <option key={tool.name} value={tool.name}>
                  {tool.displayName}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label htmlFor="language" className="block text-xs font-medium text-gray-700 mb-1">
              Language
            </label>
            <select
              id="language"
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value as any)}
              className="input-field text-xs py-1.5"
            >
              <option value="">-- Select --</option>
              {AVAILABLE_LANGUAGES.map((language) => {
                const languageNames = {
                  'ko': '한국어 (ko)',
                  'ja': '日本語 (ja)', 
                  'zh': '中文 (zh)',
                  'en': 'English (en)'
                };
                return (
                  <option key={language} value={language}>
                    {languageNames[language]}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContextSelector;
